export const getEffectiveDecayDays = (row: any): number | null => {
    const toNum = (v: any) => {
        if (v === null || v === undefined || v === '') return null;
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n : null;
    };
    const fromBackend = toNum(row?.effectiveDecayDays);
    if (fromBackend !== null) return fromBackend;
    const lod = toNum(row?.decayDaysLOD);
    const staffClient = toNum(row?.decayDaysStaffClient);
    const hoa = toNum(row?.decayDaysHOA);
    const legacy = toNum(row?.decayDays ?? row?.decay);
    const candidates = [lod, staffClient, hoa, legacy].filter(
        (v): v is number => v !== null,
    );
    if (candidates.length === 0) return null;
    return Math.max(...candidates);
};

export const getDecayBreakdown = (row: any) => ({
    LOD: row?.decayDaysLOD ?? null,
    StaffClient: row?.decayDaysStaffClient ?? null,
    HOA: row?.decayDaysHOA ?? null,
});

export const EV_FILTERS_TYPES = Object.freeze({
    TOTAL: 'TOTAL',
    ACTIVE: 'ACTIVE',
    GPS: 'GPS',
    GPS_ON: 'GPS_ON',
    GPS_OFF: 'GPS_OFF',
    GPS_R_OFF: 'GPS_R_OFF',
});
export const EV_FILTERS_TYPES_V2 = Object.freeze({
    TAGGED_ALL: 'TAGGED_ALL',
    IN_CRITICAL: 'IN_CRITICAL',
    CURRENTLY_BLOCKED: 'CURRENTLY_BLOCKED',
    PARTIALLY_BLOCKED: 'PARTIALLY_BLOCKED',
});
export const EV_FILTER_CARDS = [
    {
        name: 'Tagged EVs (all)',
        type: EV_FILTERS_TYPES_V2.TAGGED_ALL,
        filterValue: 'TAGGED_FILTER',
        valueMappingKey: 'totalCount',
        subText: 'Pan-India',
    },
    {
        name: 'In Critical',
        type: EV_FILTERS_TYPES_V2.IN_CRITICAL,
        filterValue: 'CRITICAL_FILTER',
        valueMappingKey: 'criticalCount',
        subText: 'Need action now',
        accent: 'critical',
    },
    {
        name: 'Currently Blocked',
        type: EV_FILTERS_TYPES_V2.CURRENTLY_BLOCKED,
        filterValue: 'BLOCKED_FILTER',
        valueMappingKey: 'blockedEv',
        subText: 'IoT + Energy blocked',
    },
    {
        name: 'Partially Blocked',
        type: EV_FILTERS_TYPES_V2.PARTIALLY_BLOCKED,
        filterValue: 'PARTIALLY_BLOCKED_FILTER',
        valueMappingKey: 'partiallyBlockedEv',
        subText: 'IoT or Energy blocked',
    },
];
export const EV_SEVERITY_TABS = [
    { key: 'ALL', label: 'All', dot: '', count: 0 },
    { key: 'CRITICAL', label: 'Critical', dot: '#E5484D', count: 0 },
    { key: 'HIGH', label: 'High', dot: '#F76808', count: 0 },
    { key: 'MEDIUM', label: 'Medium', dot: '#F5A623', count: 0 },
    { key: 'WATCH', label: 'Watch', dot: '#3f51b5', count: 0 },
    { key: 'HEALTHY', label: 'Healthy', dot: '#30A46C', count: 0 },
    { key: 'NOT_CALCULATED', label: 'Not calculated', dot: '#9aa0a6', count: 0 },
];

export type EvBucket = 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'WATCH' | 'HEALTHY' | 'NOT_CALCULATED';

const BUCKET_RANK: Record<EvBucket, number> = {
    NOT_CALCULATED: -1,
    HEALTHY: 0,
    WATCH: 1,
    MEDIUM: 2,
    HIGH: 3,
    CRITICAL: 4,
};

// Wallet (₹): >400 HEALTHY, 0–400 WATCH, -1 to -1,000 MEDIUM,
// -1,000 to -2,000 HIGH, < -2,000 CRITICAL.
const bucketFromWallet = (wallet: number | null): EvBucket | null => {
    if (wallet === null) return null;
    if (wallet > 400) return 'HEALTHY';
    if (wallet >= 0) return 'WATCH';
    if (wallet >= -1000) return 'MEDIUM';
    if (wallet >= -2000) return 'HIGH';
    return 'CRITICAL';
};

// Decay (days since last order): <3 HEALTHY/WATCH (resolved via other axes),
// 4–5 MEDIUM, 6–7 HIGH, >7 CRITICAL.
const bucketFromDecay = (decay: number | null): EvBucket | null => {
    if (decay === null) return null;
    if (decay < 3) return 'HEALTHY';
    if (decay <= 3) return 'WATCH';
    if (decay <= 5) return 'MEDIUM';
    if (decay <= 7) return 'HIGH';
    return 'CRITICAL';
};

// Moonlight (7-day avg %): <0 HEALTHY, 0–20 WATCH, 21–50 MEDIUM,
// 50–70 HIGH, >70 CRITICAL.
const bucketFromMoonlight = (m: number | null): EvBucket | null => {
    if (m === null) return null;
    if (m < 0) return 'HEALTHY';
    if (m <= 20) return 'WATCH';
    if (m <= 50) return 'MEDIUM';
    if (m <= 70) return 'HIGH';
    return 'CRITICAL';
};

const toNumOrNull = (v: any): number | null => {
    if (v === null || v === undefined || v === '') return null;
    const n = typeof v === 'number' ? v : Number(v);
    return Number.isFinite(n) ? n : null;
};

export const computeEvBucket = (row: any): EvBucket | null => {
    const wallet = toNumOrNull(row?.walletBalance ?? row?.wallet);
    const decay = getEffectiveDecayDays(row);
    const moonlight = toNumOrNull(
        row?.moonlightPercentage ??
            row?.evProductivity?.moonlightingDetails?.moonlightingPercentage,
    );
    const candidates = [
        bucketFromWallet(wallet),
        bucketFromDecay(decay),
        bucketFromMoonlight(moonlight),
    ].filter((b): b is EvBucket => b !== null);
    if (candidates.length === 0) return null;
    return candidates.reduce((worst, cur) =>
        BUCKET_RANK[cur] > BUCKET_RANK[worst] ? cur : worst,
    );
};

export const getEvBucket = (row: any): EvBucket => {
    const computed = computeEvBucket(row);
    if (computed) return computed;
    const raw = (row?.bucket || '').toString().toUpperCase();
    if (raw === 'LOW') return 'WATCH';
    if (raw && raw in BUCKET_RANK) return raw as EvBucket;
    return 'NOT_CALCULATED';
};
export const tableConfig = {
    columns: [
        { name: 'RIDER · MOBILE', mappingKey: 'riderMobile' },
        { name: 'BUCKET · BREACH', mappingKey: 'bucketBreach' },
        { name: 'WALLET', mappingKey: 'wallet' },
        { name: 'DECAY', mappingKey: 'decay' },
        { name: 'IOT STATUS', mappingKey: 'iotStatus' },
        { name: 'ENERGY STATUS', mappingKey: 'energyStatus' },
        { name: '', mappingKey: 'rowActions' },
    ],
};
export const CHIPS_CLASS_TYPE_MAPPING: any = Object.freeze({
    VERIFIED: 'active',
    KYC_PENDING: 'pending',
    REJECTED: 'reject',
    INACTIVE: 'inactive',
    PENDING: 'pending',
    ACTIVE: 'active',
    KYC_REJECTED: 'reject',
    TO_CX: 'active',
    IN_STORE: 'pending',
    COMPLETED: 'inactive',
    ON_ORDER: 'active',
    NO_ORDER: 'none',
    ON_BREAK: 'none',
    TO_DX: 'pending',
    OPEN: 'pending',
    RESOLVED: 'active',
    NOT_RESOLVED: 'reject',
    ASSIGNED: 'inactive',
    LIVE: 'active',
    D_PENDING: 'pending',
    D_2: 'pending',
    D_3: 'pending',
    D_4: 'reject',
    D_REJECT: 'reject',
    TAGGED: 'activeWO_BG',
    UNTAGGED: 'rejectWO_BG',
    AVAILABLE: 'inactiveWO_BG',
    RELEASED: 'inactiveWO_BG',
    EV_BIKE_DEPLOYED: 'activeWO_BG',
    RIDER_UNTAG_REQUESTED: 'activeWO_BG',
    RIDER_UNTAG_REQUESTED_REJECTED: 'rejectWO_BG',
    RETURN_DATE_UNTAG_PENDING: 'pendingWO_G',
    RETURN_APPROVAL_PENDING: 'pendingWO_G',
    VEHICLE_RETURN_PENDING: 'pendingWO_G',
    VEHICLE_RETURN_REJECTED: 'rejectWO_BG',
    PROFILE_APPROVAL_PENDING: 'pendingWO_G',
    NOT_TAGGED: 'rejectWO_BG',
    EV_REQUESTED: 'pendingWO_G',
    EV_REQUEST_REJECTED: 'rejectWO_BG',
    'EV_REQUEST REJECTED': 'rejectWO_BG',
    EV_REQUEST_APPROVED: 'activeWO_BG',
    'EV_REQUEST APPROVED': 'activeWO_BG',
    EV_DETAILS_SAVED: 'pendingWO_G',
    'EV_DETAILS SAVED': 'pendingWO_G',
    TAG_REQUEST_REJECTED: 'rejectWO_BG',
    'TAG_REQUEST REJECTED': 'rejectWO_BG',
    TAG_REQUEST_APPROVED: 'activeWO_BG',
    'TAG_REQUEST APPROVED': 'activeWO_BG',
    EV_IN_POSSESSION: 'activeWO_BG',
    EV_IN_Possession: 'activeWO_BG',
    EV_RETURN_REQUESTED: 'pendingWO_G',
    'EV_RETURN REQUESTED': 'pendingWO_G',
    EV_RETURN_REJECTED: 'rejectWO_BG',
    'EV_RETURN REJECTED': 'rejectWO_BG',
    EV_RETURN_APPROVED: 'activeWO_BG',
    'EV_RETURN APPROVED': 'activeWO_BG',
    EV_RETURNED: 'inactiveWO_BG',
    UNDERMAINTENANCE: 'rejectWO_BG',
    ACCIDENTAL: 'rejectWO_BG',
    MISSING: 'rejectWO_BG',
    IMPOUND: 'rejectWO_BG',
});

export function generateMockData(count: number) {
    const getRandomValue = (values: any) =>
        values[Math.floor(Math.random() * values.length)];
    const getRandomPercentage = () => Math.floor(Math.random() * 101);
    const getRandomDays = () => Math.floor(Math.random() * 61);
    const getRandomKm = () => Math.floor(Math.random() * 101);

    const mockData = [];

    for (let i = 0; i < count; i++) {
        const mockItem = {
            id: `SES${Math.random().toString(36).substring(7).toUpperCase()}`,
            evNumber: `SES${Math.random()
                .toString(36)
                .substring(7)
                .toUpperCase()}`,
            evVehicleNumber: `KA09${Math.random()
                .toString(36)
                .substring(2, 6)
                .toUpperCase()}`,
            vendor: getRandomValue(['eveez']),
            taggedProject: getRandomValue(['uber']),
            vehicleIgnitionStatus: getRandomValue(['ON', 'OFF', 'ROFF']),
            vehicleBattery: getRandomPercentage().toString(),
            trackingEnabled: Math.random() < 0.5,
            remoteControlEnabled: Math.random() < 0.5,
            evProductivity: {
                taggingDetails: {
                    taggedPercentage: getRandomPercentage(),
                    taggedDays: getRandomDays(),
                    totalNoOfDays: 60,
                },
                usageDetails: {
                    usedPercentage: getRandomPercentage(),
                    usedDays: getRandomDays(),
                    totalNoOfDays: 60,
                },
                goalDetails: {
                    goalPercentage: getRandomPercentage(),
                    goalAchievedDays: getRandomDays(),
                    totalNoOfDays: 60,
                },
                moonlightingDetails: {
                    moonlightingPercentage: getRandomPercentage(),
                    totalOrderKm: getRandomKm(),
                    totalEVKm: getRandomKm(),
                },
            },
            taggedRiderDetails: {
                name: getRandomValue([
                    'barjraj',
                    'ramdin verma',
                    'sharat chandran',
                    'birender mandal',
                    'amit',
                    'kushal',
                    'kasid',
                    'shiv prakash',
                    'vikram singh',
                    'sanjay',
                    'abhi',
                    'ram dutt gupta',
                    'khadak singh',
                    'gurmit singh',
                    'chanderpal',
                    'aman',
                    'khursid',
                    'rajeev',
                    'durgesh',
                    'nahar singh',
                    'Prathees',
                    'sunder paal',
                    'maansingh aswal',
                    'rohit',
                ]),
                contactNumber: `88${Math.floor(Math.random() * 1000000000)
                    .toString()
                    .padStart(8, '0')}`,
            },
            city: getRandomValue(['Bangalore']),
            evStatus: getRandomValue([
                'TAGGED',
                'UN_TAGGED',
                'AVAILABLE',
                'RELEASED',
            ]),
            orderStatus: getRandomValue([
                'live',
                'D-1',
                'D-2',
                'D-3',
                'D-4',
                'D-5',
            ]),
        };

        mockData.push(mockItem);
    }

    return mockData;
}
const normalizeIds = (arr: any, objKey: 'id' | 'value'): number[] => {
    if (!Array.isArray(arr)) return [];
    const out: number[] = [];
    for (const item of arr) {
        if (item === null || item === undefined || item === '') continue;
        let raw: any = item;
        if (typeof item === 'object') {
            raw = item?.[objKey] ?? item?.id ?? item?.value;
        }
        if (raw === null || raw === undefined || raw === '') continue;
        const n = Number(raw);
        if (!Number.isNaN(n)) out.push(n);
    }
    return out;
};

export const preProcessPayload = (payload: any) => {
    const body = {
        pageNo: payload?.pageNo,
        pageSize: payload?.pageSize,
        searchText: payload?.searchText ?? '',
        startDate: payload?.startDate ?? '',
        endDate: payload?.endDate ?? '',
        cityIds: normalizeIds(payload?.cityIds, 'value'),
        captainsIds: normalizeIds(payload?.captainsIds, 'id'),
        cmIds: normalizeIds(payload?.cmIds, 'id'),
        projectIds: normalizeIds(payload?.projectIds, 'id'),
        vendorIds: normalizeIds(payload?.vendorIds, 'id'),
        telematicIds: normalizeIds(payload?.telematicIds, 'id'),
        filter: payload?.filter ?? 'TAGGED_FILTER',
        evStatus: payload?.evStatus ?? 'TAGGED',
        sortBy: payload?.sortBy ?? null,
        sortDir: payload?.sortBy
            ? (payload?.sortDir ?? 'asc').toString().toUpperCase()
            : null,
        bucket: payload?.bucket ?? null,
    };
    return body;
};
export const openGoogleMaps = (latitude: any, longitude: any) => {
    if (!!latitude && !!longitude) {
        const url = `https://www.google.com/maps?q=${latitude},${longitude}`;
        window.open(url, '_blank');
    }
};
type RemoveKeysFromObject = <T extends object>(
    obj: T,
    keysToRemove: (keyof T)[],
) => Partial<T>;

export const removeKeysFromObject: RemoveKeysFromObject = (
    obj,
    keysToRemove,
) => {
    if (keysToRemove.length === 0) {
        return obj; // Return the original object if no keys to remove
    }
    const newObj = { ...obj };
    keysToRemove.forEach((key) => {
        delete newObj?.[key];
    });
    return newObj;
};
